<script setup lang="ts">
import {Dialog, Button, Icon, Carousel} from "@/Components/Library";
import {reactive, nextTick} from "vue";
import File from "@/Components/Global/File.vue";

const props = withDefaults(
    defineProps<{
        modelType: string;
        modelId: string | number;
        collection?: string;
        files: any;
        showDeleteButton?: boolean;
    }>(),
    {
        showDeleteButton: true,
    },
);

const emit = defineEmits(["delete"]);

const state = reactive({
    page: 0,
    show: false,
});

const showFile = async (index: number) => {
    state.page = index;
    state.show = true;
};

const getFileType = (file: any) => {
    return (
        file.type ??
        file.mime_type?.split("/").pop() ??
        file.url?.toString().split(".").pop() ??
        file.name?.toString().split(".").pop()
    );
};

const fileType = (file) => {
    if (!file) {
        return null;
    }

    const type = getFileType(file);

    if (type.match(/(jpg|jpeg|png|gif|bmp|svg|webp)/)) {
        return "image";
    }
    if (type === "pdf") {
        return "pdf";
    }
    if (type.match(/(mp3|mid|ogg|wav)/)) {
        return "audio";
    }
    if (type.match(/(webm|avi|mpeg|mp4|ogv|3gp|mkv)/)) {
        return "video";
    }
    if (type === "txt") {
        return "text";
    }

    return "other";
};

const fileIcon = (file: any) => {
    if (!file) {
        return "bi:file-earmark";
    }

    const fileType = getFileType(file);
    let fileIcon = "bi:file-earmark";

    switch (fileType) {
        //audio
        case "mp3":
        case "mid":
        case "ogg":
        case "wav":
            fileIcon = "bi:file-earmark-music";
            break;
        //video
        case "webm":
        case "avi":
        case "mpeg":
        case "mp4":
        case "ogv":
        case "3gp":
        case "mkv":
            fileIcon = "bi:file-earmark-play";
            break;
        // images
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "bmp":
        case "svg":
        case "webp":
            fileIcon = "bi:file-earmark-image";
            break;
        //docs
        case "docx":
        case "doc":
        case "xls":
        case "xlsx":
        case "ppt":
        case "pptx":
        case "txt":
        case "csv":
        case "rtf":
        case "odt":
        case "ods":
        case "odp":
            fileIcon = "bi:file-earmark-text";
            break;
        //pdf
        case "pdf":
            fileIcon = "bi:file-earmark-pdf";
            break;
        default:
            fileIcon = "bi:file-earmark";
    }

    return fileIcon;
};
</script>
<template>
    <div>
        <div v-auto-animate v-if="files.length" class="flex flex-wrap items-start gap-4">
            <template v-for="(file, index) in files" :key="file.url">
                <File
                    :file="file"
                    :modelType="modelType"
                    :modelId="modelId"
                    :collection="collection"
                    :showDeleteButton="showDeleteButton"
                    @click="showFile(index)"
                    @delete="emit('delete', file)"
                />
            </template>
        </div>
        <div v-else>
            <p>No files uploaded yet.</p>
        </div>
        <Dialog v-model:visible="state.show" :dismissableMask="true" modal maximizable header="&nbsp;">
            <Carousel
                :page="state.page"
                :value="files"
                :num-scroll="1"
                :num-visible="1"
                circular
                :prevButtonProps="{
                    class: 'absolute left-1 top-1/2 bottom-1/2 bg-white/50 -translate-y-1/2 z-50 text-2xl w-8 h-8 flex justify-center items-center',
                }"
                :nextButtonProps="{
                    class: 'absolute right-1 top-1/2 bottom-1/2 bg-white/50 -translate-y-1/2 z-50 text-2xl w-8 h-8 flex justify-center items-center',
                }"
                class="h-full w-full"
                :pt="{
                    previousbuttonicon: {class: 'w-8 h-8 mr-1'},
                    nextbuttonicon: {class: 'w-8 h-8 ml-1'},
                    content: {class: 'grow w-full flex flex-col'},
                    contentcontainer: {class: 'h-full w-full flex flex-col'},
                    viewport: {class: 'h-full w-full overflow-hidden grow'},
                    container: {class: 'h-full w-full'},
                    item: {class: 'flex shrink-0 grow flex grow w-full h-full overflow-hidden'},
                    itemList: {class: 'h-full w-full flex flex-row'},
                    // itemsContainer: {class: 'h-full w-full'},
                }"
            >
                <template #item="{data}">
                    <div class="relative flex aspect-video h-full w-full flex-col items-center justify-center">
                        <template v-if="fileType(data) === 'image'">
                            <!-- Image files -->
                            <img
                                :src="data.url"
                                :alt="data.name"
                                class="min-8-96 absolute h-full w-full object-contain object-center pb-6"
                            />
                        </template>
                        <template v-else-if="fileType(data) === 'pdf'">
                            <!-- PDF files -->
                            <h3 class="mb-4 text-xl font-semibold">{{ data.name }}</h3>
                            <iframe :src="data.url" class="h-[80vh] w-full max-w-[96rem]"></iframe>
                        </template>
                        <template v-else-if="fileType(data) === 'audio'">
                            <!-- Audio files -->
                            <div class="flex flex-col items-center justify-center py-8">
                                <h3 class="mb-4 text-xl font-semibold">{{ data.name }}</h3>
                                <audio controls class="mb-4 w-full max-w-[40rem]">
                                    <source :src="data.url" :type="`audio/${getFileType(data)}`" />
                                    Your browser does not support playing this audio.
                                </audio>
                            </div>
                        </template>
                        <template v-else-if="fileType(data) === 'video'">
                            <!-- Video files -->
                            <div class="flex flex-col items-center justify-center py-8">
                                <h3 class="mb-4 text-xl font-semibold">{{ data.name }}</h3>
                                <video controls class="mb-4 max-h-[56rem] w-full max-w-[96rem]">
                                    <source :src="data.url" :type="`video/${getFileType(data)}`" />
                                    Your browser does not support displaying this video.
                                </video>
                            </div>
                        </template>
                        <template v-else-if="fileType(data) === 'text'">
                            <!-- Text files -->
                            <div class="flex w-full flex-col items-center justify-center py-8">
                                <h3 class="mb-4 text-xl font-semibold">{{ data.name }}</h3>
                                <embed
                                    :src="data.url"
                                    class="h-[60vh] w-full max-w-[80rem] rounded-lg border border-stone-300 dark:border-stone-600"
                                    type="text/plain"
                                    frameborder="0"
                                />
                            </div>
                        </template>
                        <template v-else>
                            <!-- Other file types (fallback) -->
                            <div class="flex flex-col items-center justify-center py-8">
                                <Icon :icon="fileIcon(data)" width="96" height="96" class="mb-4" />
                                <h3 class="mb-4 text-xl font-semibold">{{ data.name }}</h3>
                                <a :href="data.url" target="_blank">
                                    <Button
                                        class="bg-askr-green text-white hover:brightness-110"
                                        :icon="{icon: 'ic:round-download', width: '24'}"
                                    >
                                        Download File
                                    </Button>
                                </a>
                            </div>
                        </template>
                    </div>
                </template>
            </Carousel>
        </Dialog>
    </div>
</template>
<style scoped></style>
